import { useEffect, useState, useCallback, useRef } from 'react';
import { audioAPI, usersAPI } from '../services/api';
import { Table, Badge, Btn, Modal, Card, Pagination, useToast, Input, Select, Spinner } from '../components/UI';

const AUDIO_ACCEPT = '.mp3,.m4a,.aac,.ogg,.opus,.wav,.webm';
const IMAGE_ACCEPT = '.jpg,.jpeg,.png,.webp';

function formatBytes(b) {
  if (!b) return '—';
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
function fmtDuration(s) {
  if (!s) return '—';
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}:${sec < 10 ? '0' + sec : sec}`;
}
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 5, color: 'var(--muted)' };

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Audio() {
  const [tab, setTab] = useState('tracks'); // 'tracks' | 'albums'

  return (
    <div style={{ padding: 28 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Audio Library</h1>
        <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>Manage albums and tracks. Role-based access controls who can listen.</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
        {['tracks', 'albums'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '8px 18px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
            background: 'none', border: 'none',
            borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent',
            color: tab === t ? 'var(--accent)' : 'var(--text2)',
            marginBottom: -1, transition: 'all .15s',
          }}>
            {t === 'tracks' ? '🎵 Tracks' : '💿 Albums'}
          </button>
        ))}
      </div>

      {tab === 'tracks' ? <TracksTab /> : <AlbumsTab />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TRACKS TAB
// ══════════════════════════════════════════════════════════════════════════════
function TracksTab() {
  const { show, ToastEl } = useToast();
  const [tracks, setTracks]         = useState([]);
  const [albums, setAlbums]         = useState([]);
  const [roles, setRoles]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [meta, setMeta]             = useState({});
  const [statusFilter, setStatus]   = useState('');
  const [albumFilter, setAlbumF]    = useState('');
  const [showCreate, setShowCreate]       = useState(false);
  const [editTrack, setEditTrack]         = useState(null);
  const [playingId, setPlayingId]         = useState(null);
  const [commentsTrackId, setCommentsId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await audioAPI.listTracks({
        page, limit: 15,
        status: statusFilter || undefined,
        album_id: albumFilter || undefined,
      });
      setTracks(data.data);
      setMeta(data.pagination);
    } catch { show('Failed to load tracks', 'error'); }
    finally { setLoading(false); }
  }, [page, statusFilter, albumFilter]);

  useEffect(() => {
    usersAPI.listRoles().then(r => setRoles(r.data.data)).catch(() => {});
    audioAPI.listAlbums({ limit: 100 }).then(r => setAlbums(r.data.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  async function del(id, title) {
    if (!window.confirm(`Delete track "${title}"? This cannot be undone.`)) return;
    try { await audioAPI.deleteTrack(id); show('Track deleted'); load(); }
    catch (e) { show(e.response?.data?.message || 'Failed to delete', 'error'); }
  }

  const columns = [
    { key: 'title', label: 'Track', render: (v, row) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 42, height: 42, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {row.thumbnail_path
            ? <img src={audioAPI.thumbnailUrl(row.id)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
            : <span style={{ fontSize: 18 }}>🎵</span>
          }
        </div>
        <div>
          <div style={{ fontWeight: 500, fontSize: 13 }}>{v}</div>
          {row.artist && <div style={{ fontSize: 11, color: 'var(--muted)' }}>{row.artist}</div>}
        </div>
      </div>
    )},
    { key: 'album_title',      label: 'Album',    render: v => <span style={{ fontSize: 12 }}>{v || '—'}</span> },
    { key: 'duration_seconds', label: 'Duration', render: fmtDuration },
    { key: 'file_size_bytes',  label: 'Size',     render: formatBytes },
    { key: 'view_count', label: 'Plays', render: v => <span style={{ fontSize: 12 }}>{v ?? 0}</span> },
    { key: 'like_count', label: 'Likes', render: (_, row) => <TrackLikeCount trackId={row.id} /> },
    { key: 'sort_order', label: 'Order', render: v => <span style={{ fontSize: 12 }}>{v ?? 0}</span> },
    { key: 'status',     label: 'Status', render: v => <Badge label={v} type={v}/> },
    { key: 'role_ids',   label: 'Roles', render: (v) => (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {(v || []).map(rid => {
          const r = roles.find(x => x.id === rid);
          return r ? <Badge key={rid} label={r.name} type="role_specific"/> : null;
        })}
      </div>
    )},
    { key: 'actions', label: '', render: (_, row) => (
      <div style={{ display: 'flex', gap: 5 }}>
        <Btn size="sm" onClick={() => setPlayingId(playingId === row.id ? null : row.id)}>
          {playingId === row.id ? '■ Stop' : '▶ Play'}
        </Btn>
        <Btn size="sm" variant="ghost" onClick={() => setCommentsId(row.id)}>💬 Comments</Btn>
        <Btn size="sm" variant="ghost" onClick={() => setEditTrack(row)}>Edit</Btn>
        <Btn size="sm" variant="danger" onClick={() => del(row.id, row.title)}>Delete</Btn>
      </div>
    )},
  ];

  return (
    <>
      {ToastEl}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Select value={statusFilter} onChange={e => { setStatus(e.target.value); setPage(1); }}
            options={[{ value: '', label: 'All statuses' }, { value: 'published', label: 'Published' }, { value: 'draft', label: 'Draft' }, { value: 'archived', label: 'Archived' }]}
            style={{ width: 160 }}
          />
          <Select value={albumFilter} onChange={e => { setAlbumF(e.target.value); setPage(1); }}
            options={[{ value: '', label: 'All albums' }, ...albums.map(a => ({ value: a.id, label: a.title }))]}
            style={{ width: 180 }}
          />
        </div>
        <Btn onClick={() => setShowCreate(true)}>+ Upload Track</Btn>
      </div>

      <Card>
        {loading
          ? <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
          : <Table columns={columns} data={tracks} emptyMsg="No tracks found." />
        }
        {!loading && meta.pages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
            <Pagination page={page} pages={meta.pages} onChange={setPage} />
          </div>
        )}
      </Card>

      {playingId && (
        <AudioPreviewBar trackId={playingId} onClose={() => setPlayingId(null)} />
      )}

      {commentsTrackId && (
        <TrackCommentsModal trackId={commentsTrackId} onClose={() => setCommentsId(null)} />
      )}

      {showCreate && (
        <TrackFormModal
          roles={roles} albums={albums}
          onClose={() => setShowCreate(false)}
          onSuccess={() => { setShowCreate(false); load(); show('Track uploaded'); }}
          onError={msg => show(msg, 'error')}
        />
      )}

      {editTrack && (
        <TrackFormModal
          track={editTrack} roles={roles} albums={albums}
          onClose={() => setEditTrack(null)}
          onSuccess={() => { setEditTrack(null); load(); show('Track updated'); }}
          onError={msg => show(msg, 'error')}
        />
      )}
    </>
  );
}

// ── Inline audio preview bar ──────────────────────────────────────────────────
function AudioPreviewBar({ trackId, onClose }) {
  const token = localStorage.getItem('accessToken');
  const src   = `${audioAPI.streamUrl(trackId)}?token=${token}`;

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 220, right: 0, zIndex: 1000,
      background: 'var(--surface)', borderTop: '2px solid var(--accent)',
      padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 12,
      boxShadow: '0 -4px 20px #0002',
    }}>
      <audio src={src} controls autoPlay style={{ flex: 1, height: 36 }} />
      <Btn size="sm" variant="ghost" onClick={onClose}>✕</Btn>
    </div>
  );
}

// ── Track form modal ──────────────────────────────────────────────────────────
function TrackFormModal({ track, roles, albums, onClose, onSuccess, onError }) {
  const isEdit = !!track;
  const [title, setTitle]           = useState(track?.title || '');
  const [artist, setArtist]         = useState(track?.artist || '');
  const [albumId, setAlbumId]       = useState(track?.album_id || '');
  const [lyrics, setLyrics]         = useState(track?.lyrics || '');
  const [status, setStatus]         = useState(track?.status || 'published');
  const [sortOrder, setSortOrder]   = useState(track?.sort_order ?? 0);
  const [selectedRoles, setSelRoles]= useState(track?.role_ids || roles.map(r => r.id));
  const [audioFile, setAudioFile]   = useState(null);
  const [thumbFile, setThumbFile]   = useState(null);
  const [saving, setSaving]         = useState(false);
  const [progress, setProgress]     = useState(0);

  const audioRef = useRef(null);
  const thumbRef = useRef(null);

  function toggleRole(id) {
    setSelRoles(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  }

  async function submit(e) {
    e.preventDefault();
    if (!isEdit && !audioFile) { onError('Please select an audio file'); return; }
    if (!title.trim()) { onError('Title is required'); return; }
    if (!selectedRoles.length) { onError('Select at least one role'); return; }

    setSaving(true); setProgress(0);
    try {
      const form = new FormData();
      form.append('title', title.trim());
      form.append('artist', artist);
      if (albumId) form.append('album_id', albumId);
      form.append('lyrics', lyrics);
      form.append('status', status);
      form.append('sort_order', sortOrder);
      selectedRoles.forEach(id => form.append('roleIds[]', id));
      if (audioFile) form.append('audio', audioFile);
      if (thumbFile) form.append('thumbnail', thumbFile);

      if (isEdit) {
        await audioAPI.updateTrack(track.id, form);
      } else {
        await audioAPI.createTrack(form, ev => setProgress(Math.round((ev.loaded / ev.total) * 100)));
      }
      onSuccess();
    } catch (err) {
      onError(err.response?.data?.message || (isEdit ? 'Update failed' : 'Upload failed'));
    } finally { setSaving(false); setProgress(0); }
  }

  return (
    <Modal title={isEdit ? 'Edit Track' : 'Upload Track'} onClose={onClose}>
      <form onSubmit={submit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Audio file */}
          <div>
            <label style={labelStyle}>AUDIO FILE {!isEdit && '*'}</label>
            <div onClick={() => audioRef.current?.click()} style={{
              border: '2px dashed var(--border)', borderRadius: 8, padding: '16px 12px',
              textAlign: 'center', cursor: 'pointer', background: 'var(--bg)',
              color: audioFile ? 'var(--text)' : 'var(--muted)', fontSize: 13,
            }}>
              {audioFile ? `✓ ${audioFile.name}` : isEdit ? 'Click to replace audio file (optional)' : 'Click to pick an audio file (mp3, ogg, m4a…)'}
            </div>
            <input ref={audioRef} type="file" accept={AUDIO_ACCEPT} style={{ display: 'none' }}
              onChange={e => {
                const f = e.target.files?.[0];
                if (!f) return;
                setAudioFile(f);
                if (!title) setTitle(f.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '));
              }}
            />
          </div>

          {/* Thumbnail */}
          <div>
            <label style={labelStyle}>THUMBNAIL IMAGE (optional)</label>
            <div onClick={() => thumbRef.current?.click()} style={{
              border: '2px dashed var(--border)', borderRadius: 8, padding: '12px',
              textAlign: 'center', cursor: 'pointer', background: 'var(--bg)',
              color: thumbFile ? 'var(--text)' : 'var(--muted)', fontSize: 13,
            }}>
              {thumbFile ? `✓ ${thumbFile.name}` : isEdit && track?.thumbnail_path ? '✓ Current thumbnail (click to replace)' : 'Click to pick thumbnail image'}
            </div>
            <input ref={thumbRef} type="file" accept={IMAGE_ACCEPT} style={{ display: 'none' }}
              onChange={e => setThumbFile(e.target.files?.[0] || null)}
            />
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}><Input label="Title *" value={title} onChange={e => setTitle(e.target.value)} placeholder="Track title" required /></div>
            <div style={{ flex: 1 }}><Input label="Artist" value={artist} onChange={e => setArtist(e.target.value)} placeholder="Artist name" /></div>
          </div>

          <Select
            label="Album (optional)"
            value={albumId}
            onChange={e => setAlbumId(e.target.value)}
            options={[{ value: '', label: '— No album —' }, ...albums.map(a => ({ value: a.id, label: a.title }))]}
          />

          <div>
            <label style={labelStyle}>LYRICS (optional)</label>
            <textarea value={lyrics} onChange={e => setLyrics(e.target.value)}
              rows={4} placeholder="Paste lyrics here…"
              style={{ width: '100%', resize: 'vertical', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13, fontFamily: 'var(--sans)', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Select label="Status" value={status} onChange={e => setStatus(e.target.value)}
                options={[{ value: 'published', label: 'Published' }, { value: 'draft', label: 'Draft' }, { value: 'archived', label: 'Archived' }]}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Input label="Sort Order" type="number" value={sortOrder} onChange={e => setSortOrder(Number(e.target.value))} min={0} />
            </div>
          </div>

          {/* Role access */}
          <div>
            <label style={labelStyle}>ACCESS — WHO CAN LISTEN *</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {roles.map(r => (
                <button key={r.id} type="button" onClick={() => toggleRole(r.id)} style={{
                  padding: '4px 14px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                  border: '1px solid var(--border)',
                  background: selectedRoles.includes(r.id) ? 'var(--accent)' : 'transparent',
                  color: selectedRoles.includes(r.id) ? '#fff' : 'var(--text)',
                }}>
                  {r.name}
                </button>
              ))}
            </div>
          </div>

          {/* Upload progress */}
          {saving && !isEdit && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--muted)', marginBottom: 5 }}>
                <span>Uploading…</span><span>{progress}%</span>
              </div>
              <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', borderRadius: 3, background: 'var(--accent)', width: `${progress}%`, transition: 'width .2s ease' }}/>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <Btn type="button" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
            <Btn type="submit" disabled={saving}>
              {saving ? (isEdit ? 'Saving…' : `Uploading ${progress}%`) : (isEdit ? 'Save Changes' : 'Upload')}
            </Btn>
          </div>
        </div>
      </form>
    </Modal>
  );
}

// ── Inline like count ─────────────────────────────────────────────────────────
function TrackLikeCount({ trackId }) {
  const [count, setCount] = useState('…');
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/audio/tracks/${trackId}/likes`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    })
      .then(r => r.json())
      .then(d => setCount(d.data?.count ?? 0))
      .catch(() => setCount('—'));
  }, [trackId]);
  return <span style={{ fontSize: 12 }}>👍 {count}</span>;
}

// ── Track comments moderation modal ──────────────────────────────────────────
function TrackCommentsModal({ trackId, onClose }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [meta, setMeta]         = useState({});
  const { show, ToastEl }       = useToast();

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const { data } = await audioAPI.listComments(trackId, { page: p, limit: 20 });
      setComments(data.data);
      setMeta(data.pagination);
    } catch { show('Failed to load comments', 'error'); }
    finally { setLoading(false); }
  }, [trackId, page]);

  useEffect(() => { load(page); }, [page]);

  async function del(commentId) {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await audioAPI.deleteComment(trackId, commentId);
      show('Comment deleted');
      load(page);
    } catch { show('Failed to delete', 'error'); }
  }

  return (
    <Modal title="Track Comments" onClose={onClose} width={600}>
      {ToastEl}
      {loading
        ? <div style={{ padding: 32, textAlign: 'center' }}><Spinner /></div>
        : comments.length === 0
          ? <p style={{ color: 'var(--muted)', textAlign: 'center', padding: 24 }}>No comments yet.</p>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {comments.map(c => (
                <div key={c.id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                  padding: '12px 0', borderBottom: '1px solid var(--border)',
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%', background: 'var(--accent2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0,
                  }}>
                    {c.user_name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>{c.user_name}</span>
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                        {new Date(c.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>{c.comment}</p>
                  </div>
                  <Btn size="sm" variant="danger" onClick={() => del(c.id)}>Delete</Btn>
                </div>
              ))}
              {meta.pages > 1 && (
                <div style={{ paddingTop: 12 }}>
                  <Pagination page={page} pages={meta.pages} onChange={p => setPage(p)} />
                </div>
              )}
            </div>
          )
      }
    </Modal>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ALBUMS TAB
// ══════════════════════════════════════════════════════════════════════════════
function AlbumsTab() {
  const { show, ToastEl } = useToast();
  const [albums, setAlbums]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [meta, setMeta]             = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [editAlbum, setEditAlbum]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await audioAPI.listAlbums({ page, limit: 15 });
      setAlbums(data.data);
      setMeta(data.pagination);
    } catch { show('Failed to load albums', 'error'); }
    finally { setLoading(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  async function del(id, title) {
    if (!window.confirm(`Delete album "${title}"? Tracks in this album will not be deleted but will be unlinked.`)) return;
    try { await audioAPI.deleteAlbum(id); show('Album deleted'); load(); }
    catch (e) { show(e.response?.data?.message || 'Failed to delete', 'error'); }
  }

  const columns = [
    { key: 'title', label: 'Album', render: (v, row) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 48, height: 48, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {row.cover_path
            ? <img src={audioAPI.albumCoverUrl(row.id)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
            : <span style={{ fontSize: 20 }}>💿</span>
          }
        </div>
        <div>
          <div style={{ fontWeight: 500, fontSize: 13 }}>{v}</div>
          {row.description && <div style={{ fontSize: 11, color: 'var(--muted)', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.description}</div>}
        </div>
      </div>
    )},
    { key: 'artist',      label: 'Artist',     render: v => <span style={{ fontSize: 12 }}>{v || '—'}</span> },
    { key: 'track_count', label: 'Tracks',     render: v => <span style={{ fontSize: 12 }}>{v ?? 0}</span> },
    { key: 'sort_order',  label: 'Order',      render: v => <span style={{ fontSize: 12 }}>{v ?? 0}</span> },
    { key: 'status',      label: 'Status',     render: v => <Badge label={v} type={v}/> },
    { key: 'created_by',  label: 'By',         render: v => <span style={{ fontSize: 12 }}>{v}</span> },
    { key: 'actions', label: '', render: (_, row) => (
      <div style={{ display: 'flex', gap: 5 }}>
        <Btn size="sm" variant="ghost"  onClick={() => setEditAlbum(row)}>Edit</Btn>
        <Btn size="sm" variant="danger" onClick={() => del(row.id, row.title)}>Delete</Btn>
      </div>
    )},
  ];

  return (
    <>
      {ToastEl}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Btn onClick={() => setShowCreate(true)}>+ New Album</Btn>
      </div>

      <Card>
        {loading
          ? <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
          : <Table columns={columns} data={albums} emptyMsg="No albums yet." />
        }
        {!loading && meta.pages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
            <Pagination page={page} pages={meta.pages} onChange={setPage} />
          </div>
        )}
      </Card>

      {showCreate && (
        <AlbumFormModal
          onClose={() => setShowCreate(false)}
          onSuccess={() => { setShowCreate(false); load(); show('Album created'); }}
          onError={msg => show(msg, 'error')}
        />
      )}

      {editAlbum && (
        <AlbumFormModal
          album={editAlbum}
          onClose={() => setEditAlbum(null)}
          onSuccess={() => { setEditAlbum(null); load(); show('Album updated'); }}
          onError={msg => show(msg, 'error')}
        />
      )}
    </>
  );
}

// ── Album form modal ──────────────────────────────────────────────────────────
function AlbumFormModal({ album, onClose, onSuccess, onError }) {
  const isEdit = !!album;
  const [title, setTitle]         = useState(album?.title || '');
  const [description, setDesc]    = useState(album?.description || '');
  const [artist, setArtist]       = useState(album?.artist || '');
  const [status, setStatus]       = useState(album?.status || 'published');
  const [sortOrder, setSortOrder] = useState(album?.sort_order ?? 0);
  const [coverFile, setCoverFile] = useState(null);
  const [saving, setSaving]       = useState(false);
  const coverRef = useRef(null);

  async function submit(e) {
    e.preventDefault();
    if (!title.trim()) { onError('Title is required'); return; }
    setSaving(true);
    try {
      const form = new FormData();
      form.append('title', title.trim());
      form.append('description', description);
      form.append('artist', artist);
      form.append('status', status);
      form.append('sort_order', sortOrder);
      if (coverFile) form.append('cover', coverFile);

      if (isEdit) {
        await audioAPI.updateAlbum(album.id, form);
      } else {
        await audioAPI.createAlbum(form);
      }
      onSuccess();
    } catch (err) {
      onError(err.response?.data?.message || (isEdit ? 'Update failed' : 'Create failed'));
    } finally { setSaving(false); }
  }

  return (
    <Modal title={isEdit ? 'Edit Album' : 'New Album'} onClose={onClose}>
      <form onSubmit={submit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Cover image */}
          <div>
            <label style={labelStyle}>COVER IMAGE (optional)</label>
            <div onClick={() => coverRef.current?.click()} style={{
              border: '2px dashed var(--border)', borderRadius: 8, padding: '14px 12px',
              textAlign: 'center', cursor: 'pointer', background: 'var(--bg)',
              color: coverFile ? 'var(--text)' : 'var(--muted)', fontSize: 13,
            }}>
              {coverFile ? `✓ ${coverFile.name}` : isEdit && album?.cover_path ? '✓ Current cover (click to replace)' : 'Click to pick cover image'}
            </div>
            <input ref={coverRef} type="file" accept={IMAGE_ACCEPT} style={{ display: 'none' }}
              onChange={e => setCoverFile(e.target.files?.[0] || null)}
            />
          </div>

          <Input label="Title *" value={title} onChange={e => setTitle(e.target.value)} placeholder="Album title" required />
          <Input label="Artist" value={artist} onChange={e => setArtist(e.target.value)} placeholder="Artist name" />
          <Input label="Description" value={description} onChange={e => setDesc(e.target.value)} placeholder="Optional description" />

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Select label="Status" value={status} onChange={e => setStatus(e.target.value)}
                options={[{ value: 'published', label: 'Published' }, { value: 'draft', label: 'Draft' }, { value: 'archived', label: 'Archived' }]}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Input label="Sort Order" type="number" value={sortOrder} onChange={e => setSortOrder(Number(e.target.value))} min={0} />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <Btn type="button" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
            <Btn type="submit" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Album'}
            </Btn>
          </div>
        </div>
      </form>
    </Modal>
  );
}
